import axiosInstance from "../../../middlewares/axiosInstance";
import { AxiosResponse } from "axios";
import { IESDoc } from "../../../app.interface";

interface ListParams {
  searchQuery?: string;
  filters?: any[];
  sortField?: string;
  sortOrder?: "asc" | "desc";
  pageIndex?: number;
  pageSize?: number;
}

interface ListResponse {
  data: IESDoc[];
  metadata: {
    total: {
      value: number;
    };
    took: number;
    timed_out: boolean;
    max_score: number | null;
  };
}

interface Role {
  _id?: string;
  name: string;
  description: string;
  permissions: string[];
  is_system_role: boolean;
}

// User Permissions Update Interfaces
interface UserRoleAssignment {
  module: string;
  location_id: string;
  id_type: "branch" | "warehouse" | "department";
  role_id: string;
  assigned_at: number;
  assigned_by: string;
}

interface UserLocationPermissions {
  location_id: string;
  id_type: "branch" | "warehouse" | "department";
  permissions: string[];
}

interface UserPermissionsUpdate {
  [module: string]: UserLocationPermissions[];
}

interface UpdateUserPermissionsRequest {
  module: string;
  target_user_id: string;
  newRoles: UserRoleAssignment[];
  newPermissions: UserPermissionsUpdate;
}

interface UpdateUserPermissionsResponse {
  success: boolean;
  message: string;
}

class ConfigService {
  // Role Management
  async listRoles(params: ListParams): Promise<ListResponse> {
    const response = await axiosInstance.post(
      "/business-admin/user-roles/list",
      {
        ...params,
      }
    );
    return response.data.data;
  }

  async getRole(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/user-roles/get", { _id: id });
  }

  async createRole(data: Role): Promise<AxiosResponse<any>> {
    const dataToSend = {
      name: data.name,
      permissions: data.permissions,
    };
    const response = await axiosInstance.post(
      "/business-admin/user-roles/create",
      dataToSend
    );
    return response.data;
  }

  async updateRole(data: Role): Promise<AxiosResponse<any>> {
    const dataToSend = {
      name: data.name,
      permissions: data.permissions,
      _id: data._id,
    };
    return axiosInstance.post("/business-admin/user-roles/update", dataToSend);
  }

  async deleteRole(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/user-roles/delete", { _id: id });
  }

  // Permission Management
  async listPermissions(params: any): Promise<AxiosResponse<any>> {
    const response = await axiosInstance.post(
      "/business-admin/permissions/list",
      {
        ...params,
      }
    );
    return response.data.data;
  }

  // System Configuration
  async getSystemConfig(): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/config/get");
  }

  async updateSystemConfig(data: any): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/config/update", data);
  }

  async listBranches(params: ListParams): Promise<ListResponse> {
    const response = await axiosInstance.post("/business-admin/branches/list", {
      ...params,
    });
    return response.data.data;
  }

  // User Permissions Management
  async updateUserPermissions(
    data: UpdateUserPermissionsRequest
  ): Promise<UpdateUserPermissionsResponse> {
    const response = await axiosInstance.post(
      "/business-admin/permissions/update-user-permissions",
      data
    );
    return response.data;
  }
}

export const configService = new ConfigService();

// Export interfaces for use in other components
export type {
  UpdateUserPermissionsRequest,
  UpdateUserPermissionsResponse,
  UserRoleAssignment,
  UserLocationPermissions,
  UserPermissionsUpdate,
};
